/** Browser mirror of src/brain/transcript.ts. Edit the canonical file; webMirrorContracts pins the body. */
const EXIT_PLAN_MODE_TOOL: typeof import('../../src/shared/planTool.js').EXIT_PLAN_MODE_TOOL = 'ExitPlanMode';
const SHARE_IMAGE_TOOL: typeof import('../../src/shared/coreToolNames.js').SHARE_IMAGE_TOOL = 'ShareImage';
const SHARE_FILE_TOOL: typeof import('../../src/shared/coreToolNames.js').SHARE_FILE_TOOL = 'ShareFile';

type BrainCard = import('../../src/shared/wireContract.js').BrainCard;
type BrainEvent = import('../../src/shared/wireContract.js').BrainEvent;
type ToolOutputView = import('../../src/shared/wireContract.js').ToolOutputView;
type BrainMessageImage = import('../../src/shared/wireContract.js').BrainMessageImage;
type BrainMessageFile = import('../../src/shared/wireContract.js').BrainMessageFile;
type BrainSubagentView = import('../../src/shared/wireContract.js').BrainSubagentView;
type ChatEventView = import('../../src/shared/wireContract.js').ChatEventView;
type VoiceCallRecord = import('../../src/shared/wireContract.js').VoiceCallRecord;
type MessageBlockView = import('../../src/shared/wireContract.js').MessageBlockView;
type BrainMessageView = import('../../src/shared/wireContract.js').BrainMessageView;
type BrainAddress = import('../../src/shared/wireContract.js').BrainAddress;
type ReactionDetails = Extract<import('../../src/shared/wireContract.js').ChatEventDetails, { kind: 'reaction' }>;

/** The CLI/web projection of a live `workflow` snapshot — structurally the event payload itself (a
 *  workflow event carries the WHOLE DAG each time), so it aliases WorkflowUpdate to stay single-source
 *  with the wire contract. `TranscriptModel.workflows()` keeps the latest one per id. */
export type WorkflowState = import('../../src/shared/wireContract.js').BrainWorkflowView;
export type WorkflowNode = import('../../src/shared/wireContract.js').WorkflowNode;

/** Browser-safe data projection shared by the web and terminal. Rendering stays surface-specific. */
export interface ToolItem { name: string; detail?: string; diff?: string; output?: ToolOutputView; id?: string; command?: string; sub?: SubagentState;
  /** The workflow DAG a `WorkflowStart` call is running, attached by its tool call id exactly as `sub`
   *  is for a delegate call. Durable: this is what the panel projection is rebuilt from on every
   *  hydration, so it is also what lets a finished workflow still open its modal from the transcript. */
  wf?: WorkflowState;
  /** The markdown an `ExitPlanMode` call submitted, attached by its tool call id like `sub`/`wf`. It is
   *  what the plan panel renders and what raises the "implement it?" decision — a submitted plan is a
   *  tool CALL, never a shape recognized in the model's prose. Read it with {@link submittedPlanOf}. */
  plan?: string;
  questionAnswer?: import('../../src/shared/wireContract.js').QuestionAnswerRecord;
  /** Live rolling tail of a still-running `Bash` (from the `tool_progress` event), shown under the
   *  tool row while it streams. LIVE-only — never persisted; the final `output`/`diff` clears it. */
  progress?: string;
  /** Producer lifecycle, independent of surrounding turn streaming and output presence. */
  pending?: boolean; startedAt?: number; durationMs?: number; reason?: string }

/** The plan a tool item submitted, or undefined when it submitted none. Keyed on the tool name as well
 *  as the field, so no other tool can raise a plan panel by shipping a `plan` of its own. */
export function submittedPlanOf(item: ToolItem): string | undefined {
  if (item.name !== EXIT_PLAN_MODE_TOOL) return undefined;
  return item.plan?.trim() ? item.plan : undefined;
}

/** Live progress of a delegated sub-agent, attached to its `delegate` tool item by call id — what the
 *  CLI renders as the `↳ …` line under the tool row (current child tool, counters, drill-in target).
 *  Structurally the wire's own sub-agent view, so it aliases it for the same reason WorkflowState
 *  aliases WorkflowUpdate: a field added to the durable row must reach the CLI without a second edit.
 *  It was a hand-copy of all eighteen fields until jscpd showed the two drifting apart. */
export type SubagentState = BrainSubagentView;
type Segment =
  | { kind: 'text'; text: string }
  /** The model's reasoning/thinking stream — rendered dim + separate from the answer. */
  | { kind: 'reasoning'; text: string }
  | { kind: 'tools'; items: ToolItem[] }
  | { kind: 'image'; image: BrainMessageImage; caption?: string; preview?: true; deliveredAt?: number }
  | { kind: 'file'; file: BrainMessageFile; caption?: string; deliveredAt?: number }
  | ({ kind: 'view' } & import('../../src/shared/interactiveView.js').InteractiveViewDisplay)
  /** A reply's message blocks; `id` is the stored entry id, which dedups the live event against history. */
  | { kind: 'blocks'; id: string; blocks: MessageBlockView[] };
/** A rendered tool group: consecutive items of the SAME tool that carry no diff, no output block, no
 *  sub-agent and no console command fold into ONE visual row showing the LAST item's detail plus a
 *  `×count` when >1 (the Claude-Code collapsed look — repeated Read/List/Grep of files). `count` is the
 *  run length; `item` is the newest item in the run so its detail updates in place as calls stream.
 *
 *  Grouping lives in the RENDERER, not the model: the model keeps every item separate so id-keyed
 *  diff/tool_output/subagent attachment still lands on the right item, and resumed
 *  history collapses for free. An item WITH a diff/output/sub/command stays its own group (count 1) —
 *  it renders its own block, and a shell command's verbatim text is meaningful per call. */
export interface ToolGroup {
  item: ToolItem;
  count: number;
  /** A folded group retains every running call while displaying the latest detail. */
  running?: ToolItem;
  /** Every item of a folded run of FAILED tool results. A bare-row run needs only its count (the rows are
   *  identical), but each failure carries its own message — the path it refused — so the renderer keeps
   *  them all to list on expand. */
  members?: ToolItem[];
}

/** True when an item is a bare tool row (no block of its own), the only kind that collapses. A live
 *  `progress` tail is a block of its own, so a streaming command never folds into a collapsed run. */
function isCollapsibleTool(item: ToolItem): boolean {
  return !item.diff && !item.output && !item.sub && !item.wf && !item.plan && !item.questionAnswer && !item.command && !item.progress;
}

/** The kind of failure a tool result is, or undefined when it is not one. Four refusals that differ only
 *  by the file they name are ONE failure repeated, and reading them as such is what lets the transcript
 *  show a single row instead of four identical blocks. So the signature is the message with its varying
 *  parts — paths and numbers — flattened away, under the tool that produced it.
 *
 *  Only `result` outputs fold. A console command's output is the thing you actually want to read when it
 *  fails (the failing test, the stack trace), and it is different every time; collapsing that would hide
 *  the one output worth showing. */
export function failureSignature(item: ToolItem): string | undefined {
  const output = item.output;
  if (!output || output.kind !== 'result') return undefined;
  if (output.tone !== 'warning' && output.tone !== 'danger') return undefined;
  const firstLine = (output.text ?? '').split('\n').find((line) => line.trim()) ?? '';
  const shape = firstLine.replace(/\S*\/\S+/g, '§').replace(/\d+/g, '#').replace(/\s+/g, ' ').trim().slice(0, 160);
  return `${item.name}|${shape}`;
}

/** Fold a tools segment's items into render groups (see {@link ToolGroup}). Pure — recomputed every
 *  render, so a streaming row's count and latest detail stay live. Shared by both CLI renderers. */
export function groupToolItems(items: ToolItem[]): ToolGroup[] {
  const groups: ToolGroup[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    // Every explicit delivery attempt retains its own target and refusal, live and after reload.
    if (item.name === SHARE_IMAGE_TOOL || item.name === SHARE_FILE_TOOL || item.name === 'ShowView') {
      groups.push({ item, count: 1, ...(item.pending ? { running: item } : {}) });
      continue;
    }
    if (last && isCollapsibleTool(item) && isCollapsibleTool(last.item) && last.item.name === item.name) {
      groups[groups.length - 1] = { item, count: last.count + 1, running: last.running ?? (item.pending ? item : undefined) }; // latest detail wins, count grows
      continue;
    }
    const signature = failureSignature(item);
    if (signature && last && signature === failureSignature(last.item)) {
      groups[groups.length - 1] = {
        item,
        count: last.count + 1,
        members: [...(last.members ?? [last.item]), item],
      };
      continue;
    }
    groups.push({ item, count: 1, ...(item.pending ? { running: item } : {}) });
  }
  return groups;
}

type YouTurn = Pick<BrainMessageView, 'author' | 'deletedAuthorId' | 'mentions' | 'reactions' | 'replyTo' | 'settled' | 'branchDepth' | 'attachments'> & { role: 'you'; text: string; id?: string; images?: BrainMessageImage[]; voiceCall?: VoiceCallRecord; createdAt?: string };
export type ElowenTurn = Pick<BrainMessageView, 'replyTo' | 'mentions' | 'reactions' | 'settled' | 'branchDepth'> & { role: 'elowen'; segments: Segment[]; streaming: boolean; id?: string; synthetic?: true; createdAt?: string; durationMs?: number; model?: string;
  /** Set on the FIRST step of a native turn that a delivery started: a delegated or workflow result
   *  (with or without its visible finish marker), a child's background-jobs-finished notice, or a
   *  background process exit wake-up. Absent on every other step and on turns a person, a schedule or
   *  any other hidden input started. Live and hydrated folds set it identically. On the step that
   *  carries `durationMs`, `createdAt` is the native turn's completion time. */
  trigger?: 'delivery';
  /** This step ends in tool rows and the next one opens with them, so the CLI may omit this turn's
   *  separator and let the two read as one run while preserving both independent step blocks.
   *  `thoughts-hidden` keeps the separator when reasoning rows are enabled. Display-only and derived again
   *  on history hydration. */
  joinNextToolRun?: true | 'thoughts-hidden';
  /** True while the model is writing a tool call whose marker has not yet rendered — a live-only hint set
   *  by `tool_authoring` and cleared by the first `tool` of the turn. Never persisted (history turns are
   *  never streaming). */
  composing?: boolean;
  /** The name of the tool currently being authored, known from the `toolcall_start` block before its
   *  arguments finish streaming — lets the composing hint show the real tool. Cleared alongside `composing`. */
  composingTool?: string;
  /** The salient argument of the tool being authored (file path, command, query…), as it streams in — lets
   *  a long-duration tool's composing hint show a localized action label with its target. Cleared alongside
   *  `composing`. */
  composingDetail?: string;
  /** The model-authored `reason` note for the tool being authored, as it streams in — when set it supersedes
   *  the localized composing label (the CLI shows it verbatim). Cleared alongside `composing`. */
  composingReason?: string };
/** A context-compaction boundary: everything before it was summarized away, so the surface renders a
 *  subtle "context compacted" divider in its place followed by the kept tail (see `persistCompaction`). */
type DividerTurn = { role: 'divider'; id?: string };
/** One stored chat event row (see ChatEventView). `id` is its journal entry id, which dedups a live-folded
 *  event against the same row already seeded from history. */
export interface SessionEventItem { id: string; event: ChatEventView }
/** A run of chat event rows, in the conversation's own order. Consecutive rows (switching model AND mode
 *  before speaking again) collapse into ONE turn holding them all, exactly as consecutive tool calls collapse into one segment: they then stack
 *  as a block, and the single blank every turn ends with separates the block from what follows instead of
 *  falling between each pair. Marker rows read as tool rows, so the block also joins an adjacent tool run
 *  through `joinNextToolRun`, with the same meaning as on an assistant step. */
type EventTurn = { role: 'event'; events: SessionEventItem[]; id?: string; joinNextToolRun?: true | 'thoughts-hidden' };
/** Optional `id` is the source journal entry id: a stable browser key and the identity that
 *  prependHistory deduplicates. Durable user and event ids can also arrive live.
 *  A live assistant turn never carries one; only assistant turns built from stored history do.
 *  Ids never come from text fingerprints. */
export type ChatTurn = YouTurn | ElowenTurn | DividerTurn | EventTurn;

/** One stored turn as `turnsFromHistory` consumes it. Structurally the `BrainMessageView` the daemon serves
 *  (`GET /brain/messages`) and the web's `BrainMessage` — a flat `text` plus optional ordered `segments`. */
export interface HistoryMessage extends Pick<BrainMessageView, 'author' | 'deletedAuthorId' | 'replyTo' | 'mentions' | 'reactions' | 'settled' | 'branchDepth' | 'attachments'> {
  role: string;
  text: string;
  segments?: import('../../src/shared/wireContract.js').BrainSegment[];
  /** The source's own journal entry id. Absent only when the source had none. */
  id?: string;
  synthetic?: boolean;
  trigger?: 'delivery';
  /** Present on a `role:'event'` row: the stored chat event it shows. */
  event?: ChatEventView;
  createdAt?: string;
  durationMs?: number;
  model?: string;
  images?: BrainMessageImage[];
  voiceCall?: VoiceCallRecord;
}

/** Whether a segment puts any row on screen. Empty text and an item-less tool segment render nothing at
 *  all; reasoning renders only while the CLI shows thought rows, so it cannot be judged from storage shape
 *  alone. */
function segmentVisibility(segment: Segment): 'always' | 'thoughts-only' | 'never' {
  if (segment.kind === 'tools') return segment.items.length > 0 ? 'always' : 'never';
  if (segment.kind === 'reasoning') return 'thoughts-only';
  // A Read preview image is drawn under its tool row by the web and skipped by the CLI, so it is never a
  // row of its own between two tool runs.
  if (segment.kind === 'image') return segment.preview ? 'never' : 'always';
  if (segment.kind === 'file' || segment.kind === 'view' || segment.kind === 'blocks') return 'always';
  return segment.text.trim() ? 'always' : 'never';
}

/** Whether the given EDGE of a step — the first rows it paints, or the last ones — is tool rows.
 *
 *  This is deliberately an edge test, not a whole-turn test. The separator under question sits between the
 *  LAST row of one step and the FIRST row of the next, so only those two edges can touch it. Prose earlier
 *  in a step is irrelevant to its trailing seam: it already got its own separator from the in-turn segment
 *  break, and counting it again is what put a blank line between a step's closing tool row and the next
 *  step's opening one. */
function toolEdge(turn: ChatTurn | undefined, side: 'head' | 'tail'): true | 'thoughts-hidden' | undefined {
  // Marker rows (model, cwd, sub-agent done…) are drawn in the tool column in the tool-row style.
  if (turn?.role === 'event') return true;
  if (turn?.role !== 'elowen') return undefined;
  const segments = side === 'head' ? turn.segments : [...turn.segments].reverse();
  // The edge segment differs by thought setting, so resolve it for both: hidden thoughts skip reasoning,
  // shown thoughts do not. A reasoning row (with its own surrounding blanks) is a real boundary.
  const whenShown = segments.find((segment) => segmentVisibility(segment) !== 'never');
  const whenHidden = segments.find((segment) => segmentVisibility(segment) === 'always');
  if (whenHidden?.kind !== 'tools') return undefined;
  return whenShown?.kind === 'tools' ? true : 'thoughts-hidden';
}

/** The separator mode for the seam between two adjacent steps, shared by history hydration and the live
 *  fold. Tool rows on both sides of the seam are one continuous run of machine activity and take no blank
 *  line; anything else there is a real boundary and keeps its separator. */
export function toolRunJoinMode(
  previous: ChatTurn | undefined,
  next: ChatTurn | undefined,
): true | 'thoughts-hidden' | undefined {
  const before = toolEdge(previous, 'tail');
  const after = toolEdge(next, 'head');
  if (!before || !after) return undefined;
  return before === true && after === true ? true : 'thoughts-hidden';
}

/** Parse the durable wire/storage transcript into render turns without adding runtime bookkeeping. */
export function turnsFromHistory(msgs: HistoryMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (const m of msgs) {
    if (m.role === 'compaction') { turns.push({ role: 'divider', ...(m.id ? { id: m.id } : {}) }); continue; }
    if (m.role === 'event') {
      if (!m.event) continue;
      if (m.event.details.kind === 'reaction') { applyReaction(turns, m.event.details); continue; }
      const item: SessionEventItem = { id: m.event.id, event: m.event };
      const tail = turns[turns.length - 1];
      if (tail?.role === 'event') tail.events.push(item);
      else turns.push({ role: 'event', events: [item], id: m.id });
      continue;
    }
    if (m.role === 'user') {
      if (m.text.trim() || m.images?.length || m.attachments?.length || m.voiceCall) turns.push({ role: 'you', text: m.text, settled: m.settled, ...(m.branchDepth !== undefined ? { branchDepth: m.branchDepth } : {}), ...(m.author ? { author: m.author } : {}), ...(m.deletedAuthorId !== undefined ? { deletedAuthorId: m.deletedAuthorId } : {}), ...(m.replyTo ? { replyTo: m.replyTo } : {}), ...(m.mentions ? { mentions: m.mentions } : {}), ...(m.reactions ? { reactions: m.reactions } : {}), ...(m.id ? { id: m.id } : {}), ...(m.images?.length ? { images: m.images } : {}), ...(m.attachments?.length ? { attachments: m.attachments } : {}), ...(m.voiceCall ? { voiceCall: m.voiceCall } : {}), ...(m.createdAt ? { createdAt: m.createdAt } : {}) });
      continue;
    }
    const segments: Segment[] = [];
    for (const seg of m.segments ?? (m.text.trim() ? [{ kind: 'text' as const, text: m.text }] : [])) {
      if (seg.kind === 'text') {
        segments.push({ kind: 'text', text: seg.text });
      } else if (seg.kind === 'image') {
        segments.push({ kind: 'image', image: seg.image, ...(seg.caption ? { caption: seg.caption } : {}), ...(seg.preview ? { preview: true } : {}), ...(seg.deliveredAt !== undefined ? { deliveredAt: seg.deliveredAt } : {}) });
      } else if (seg.kind === 'view' || seg.kind === 'blocks') {
        segments.push(seg);
      } else if (seg.kind === 'file') {
        segments.push({ kind: 'file', file: seg.file, ...(seg.caption ? { caption: seg.caption } : {}), ...(seg.deliveredAt !== undefined ? { deliveredAt: seg.deliveredAt } : {}) });
      } else {
        const item: ToolItem = { name: seg.name, id: seg.id, detail: seg.detail, diff: seg.diff, output: seg.output, command: seg.command, sub: seg.sub, wf: seg.wf, plan: seg.plan, questionAnswer: seg.questionAnswer, pending: seg.pending ?? false, startedAt: seg.startedAt, durationMs: seg.durationMs, reason: seg.reason };
        const tail = segments[segments.length - 1];
        if (tail?.kind === 'tools') tail.items.push(item);
        else segments.push({ kind: 'tools', items: [item] });
      }
    }
    if (segments.length > 0) turns.push({
      role: 'elowen', segments, streaming: false, settled: m.settled,
      ...(m.branchDepth !== undefined ? { branchDepth: m.branchDepth } : {}),
      ...(m.id ? { id: m.id } : {}),
      ...(m.synthetic ? { synthetic: true as const } : {}),
      ...(m.createdAt ? { createdAt: m.createdAt } : {}),
      ...(m.durationMs != null ? { durationMs: m.durationMs } : {}),
      ...(m.model ? { model: m.model } : {}),
      ...(m.trigger ? { trigger: m.trigger } : {}),
      ...(m.replyTo ? { replyTo: m.replyTo } : {}),
      ...(m.mentions ? { mentions: m.mentions } : {}),
      ...(m.reactions ? { reactions: m.reactions } : {}),
    });
  }
  for (let index = 0; index + 1 < turns.length; index += 1) {
    const turn = turns[index];
    if (turn?.role !== 'elowen' && turn?.role !== 'event') continue;
    const join = toolRunJoinMode(turn, turns[index + 1]);
    if (join) turn.joinNextToolRun = join;
  }
  return turns;
}

/** Fold a live `card` event into a card list: replace by id, append when new, or drop when the card came
 *  back empty (a cleared panel). Shared by every surface's card region. */
export function upsertCard(cards: BrainCard[], card: BrainCard): BrainCard[] {
  const rest = cards.filter((c) => c.id !== card.id);
  return (!card.items?.length && !card.body) ? rest : [...rest, card];
}
export type TranscriptEvent = Exclude<BrainEvent, { type: 'workflow' }> | ({ type: 'workflow' } & WorkflowState);
export interface ChatView { turns: ChatTurn[]; thinking: boolean; notice?: string; compacting?: boolean;
  /** Snapshot-owned inert suffix, displayed after the run but excluded from every live event fold. */
  conversationAppendCount?: number;
  /** Native retry sidecar; absent before a retry is observed, null after its run settles. */
  modelRetry?: import('../../packages/plugin-shared/retry.mjs').RetryProgress | null;
  /** Consumed by the first assistant step only, never by a display marker. */
  pendingTrigger?: 'delivery'; boundaryIds?: readonly string[];
  /** Validated successful settlement, consumed by the next nonempty assistant text only. */
  pendingAddress?: BrainAddress;
}
/** @externalConsumer Byte-pinned browser mirror uses this canonical export in BrainChatProvider. */
export const emptyView = (): ChatView => ({ turns: [], thinking: false });
export const fromHistory = (messages: HistoryMessage[]): ChatView => ({ turns: turnsFromHistory(messages), thinking: false });
/** Byte-pinned mirror of the pure shared map: the web root cannot import package runtime code. */
const MIME_BY_EXTENSION: typeof import('../../packages/plugin-shared/imageMime.mjs').MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

/** Turn the wire `image` event's `ref` into the SAME {@link BrainMessageImage} a stored image segment
 *  carries, so the renderer has one shape to draw and one prefix rule to apply. The event's ref is a
 *  browser-facing path (`/api/brain/chat-images/x.png`) while a
 *  stored segment holds the bare daemon path — strip the prefix here rather than teaching the renderer
 *  that a live image is addressed differently from a reloaded one. The mime type is not on the wire, so
 *  it is read off the extension the daemon constrains to png/jpg/gif/webp. */
function imageFromRef(ref: string): BrainMessageImage {
  const url = ref.startsWith('/api/') ? ref.slice('/api'.length) : ref;
  const extension = url.slice(url.lastIndexOf('.') + 1).toLowerCase();
  return { url, mimeType: MIME_BY_EXTENSION[extension] ?? 'application/octet-stream' };
}


function isSubagentToolName(name: string): boolean {
  return name === 'Delegate' || name === 'DelegateContinue';
}

function anchorKeyOf(item: ToolItem): string | undefined {
  if (!item.id) return undefined;
  if (isSubagentToolName(item.name)) return `subagent:${item.id}`;
  return item.name === 'WorkflowStart' ? `workflow:${item.id}` : undefined;
}

function runningAnchorKeys(turn: ChatTurn): string[] {
  if (turn.role !== 'elowen') return [];
  return turn.segments.flatMap((segment) => segment.kind === 'tools'
    ? segment.items.flatMap((item) => {
        const key = anchorKeyOf(item);
        return key ? [key] : [];
      })
    : []);
}

/** Prepend an older page of stored history (chat lazy-load, scroll-up) in front of the current view. Turns
 *  already present are dropped by `id` so a re-fetched or overlapping page can't double a turn; the live
 *  streaming tail is never touched (older turns only ever go in front).
 *
 *  First-page snapshots may contain synthetic Delegate/WorkflowStart anchors for running work whose real
 *  tool row sits on an older page. When pagination reaches that row, replace the synthetic turn by tool-call
 *  id before prepending it. Otherwise both copies survive (their message ids differ), and a later terminal
 *  event patches only one of them, leaving a phantom running row in the panel. */
export function prependHistory(view: ChatView, older: HistoryMessage[]): ChatView {
  return prependOlderTurns(view, fromHistory(older).turns);
}

/** The turn-level core of {@link prependHistory}, shared with {@link keepOlderWindow}. Never mutates either
 *  input: `incoming` may be turns that are already rendered, so a replaced anchor's newer state goes onto a
 *  copy of its real tool item. */
function prependOlderTurns(view: ChatView, incoming: readonly ChatTurn[]): ChatView {
  const realTools = new Map(incoming.flatMap((turn) => turn.role === 'elowen' && !turn.synthetic
    ? turn.segments.flatMap((segment) => segment.kind === 'tools' ? segment.items : [])
      .flatMap((item) => { const key = anchorKeyOf(item); return key ? [[key, item] as const] : []; })
    : []));
  const patched = new Map<ToolItem, ToolItem>();
  const withoutReplaced = view.turns.filter((turn) => {
    if (turn.role !== 'elowen' || !turn.synthetic) return true;
    const keys = runningAnchorKeys(turn);
    if (!keys.length || !keys.every((key) => realTools.has(key))) return true;
    for (const segment of turn.segments) {
      if (segment.kind !== 'tools') continue;
      for (const item of segment.items) {
        const key = anchorKeyOf(item);
        const real = key ? realTools.get(key) : undefined;
        if (!real) continue;
        const next = { ...(patched.get(real) ?? real) };
        if (item.sub && (!next.sub || next.sub.status === 'running' || item.sub.status !== 'running')) next.sub = item.sub;
        if (item.wf && (!next.wf || next.wf.status === 'running' || item.wf.status !== 'running')) next.wf = item.wf;
        patched.set(real, next);
      }
    }
    return false;
  });
  const retained = withoutReplaced.length === view.turns.length ? view.turns : withoutReplaced;
  const older = patched.size === 0 ? incoming : incoming.map((turn) => turn.role === 'elowen'
    && turn.segments.some((segment) => segment.kind === 'tools' && segment.items.some((item) => patched.has(item)))
    ? { ...turn, segments: turn.segments.map((segment) => segment.kind === 'tools'
      ? { ...segment, items: segment.items.map((item) => patched.get(item) ?? item) }
      : segment) }
    : turn);

  const known = new Set<string>();
  for (const turn of retained) if (turn.id) known.add(turn.id);
  const prepend = older.filter((turn) => !turn.id || !known.has(turn.id));
  if (prepend.length === 0 && retained === view.turns) return view;
  return { ...view, turns: [...prepend, ...retained] };
}

/** Apply a newest-window replacement (a stream snapshot after a reconnect, or a newest-page history read)
 *  without dropping the older pages a reader lazy-loaded above it. `fresh` is the replacement view and its
 *  first stored turn is the first turn carrying a journal entry id; synthetic running anchors are skipped,
 *  because their ids are derived, not entries. When that id sits at index k > 0 of `view`, the result is
 *  `view.turns[0..k)` in front of `fresh.turns`, with every other field exactly as `fresh` sets it; otherwise
 *  it is `fresh` itself, so `result !== fresh` means the older window was kept.
 *
 *  Journal parent links are immutable, so the ancestors of an entry still on the active branch are
 *  unchanged: the turns in front of it on screen are still valid. An id missing from `view` (a rewind past
 *  the window, another session, an empty view, or a page boundary inside a run of event rows, which fold
 *  under the run's first id) proves nothing older, and neither does an id at index 0. Two parts of the old
 *  window are live state rather than entries: its synthetic anchors are dropped, since `fresh` re-derives
 *  every anchor still needed and the shared prepend folds them into retained real rows; and a retained row
 *  that still shows running work `fresh` no longer anchors missed its terminal event while the client was
 *  disconnected, so the whole window is replaced as before rather than keep a phantom running row.
 *  @externalConsumer Byte-pinned browser mirror uses this canonical export in brainChatHistory. */
export function keepOlderWindow(view: ChatView, fresh: ChatView): ChatView {
  const freshEnd = fresh.turns.length - (fresh.conversationAppendCount ?? 0);
  const currentEnd = view.turns.length - (view.conversationAppendCount ?? 0);
  const first = fresh.turns.find((turn, index) => index < freshEnd && turn.id !== undefined && !(turn.role === 'elowen' && turn.synthetic));
  const end = first ? view.turns.findIndex((turn, index) => index < currentEnd && turn.id === first.id) : -1;
  if (end <= 0) return fresh;
  const older = view.turns.slice(0, end).filter((turn) => turn.role !== 'elowen' || !turn.synthetic);
  const anchored = new Set(fresh.turns.flatMap(runningAnchorKeys));
  const staleRunning = older.some((turn) => turn.role === 'elowen' && turn.segments.some((segment) => segment.kind === 'tools'
    && segment.items.some((item) => {
      if (item.sub?.status !== 'running' && item.wf?.status !== 'running') return false;
      const key = anchorKeyOf(item);
      return !key || !anchored.has(key);
    })));
  return staleRunning ? fresh : prependOlderTurns(fresh, older);
}

/** Rebuild the entire view from a stream snapshot frame: the durable history plus the current run's
 *  not-yet-durable tail, folded in order. REPLACES the view — it is never merged into what is already
 *  rendered, because the server withholds from `history` exactly those user rows it replays as ordering
 *  markers in `events`. Merging (or pairing this with a separate history fetch) doubles those bubbles.
 *  @externalConsumer Byte-pinned browser mirror uses this canonical export in BrainChatProvider. */
export function fromSnapshot(snapshot: { history: HistoryMessage[]; events: BrainEvent[]; conversationAppends?: HistoryMessage[] }): ChatView {
  let view = fromHistory(snapshot.history);
  for (const event of snapshot.events) view = reduce(view, event);
  // The server owns pending append identity and its atomic transition into history. They are display
  // rows, never user echoes: replay and all subsequent live frames continue before this suffix.
  const appends = turnsFromHistory(snapshot.conversationAppends ?? []);
  return appends.length ? { ...view, turns: [...view.turns, ...appends], conversationAppendCount: appends.length } : view;
}

/** The shared history/live reaction projection. Viewer-specific mine is derived by the browser. */
export function reactionChipsAfter(
  current: BrainMessageView['reactions'], reaction: ReactionDetails,
): NonNullable<BrainMessageView['reactions']> {
  const chips = current ?? [];
  const actor = reaction.actor.kind === 'agent' ? { id: 'agent' as const, name: 'Chetty' }
    : { id: reaction.actor.id, name: reaction.actor.name };
  const index = chips.findIndex(chip => chip.emoji === reaction.emoji);
  const chip = chips[index];
  const present = chip?.reactors.some(reactor => reactor.id === actor.id) === true;
  if (present === (reaction.action === 'add')) return chips;
  const reactors = reaction.action === 'add' ? [...(chip?.reactors ?? []), actor]
    : chip!.reactors.filter(reactor => reactor.id !== actor.id);
  const next = { emoji: reaction.emoji, count: reactors.length, reactors, mine: false };
  if (index < 0) return [...chips, next];
  return chips.flatMap((existing, cursor) => cursor !== index ? [existing] : reactors.length ? [next] : []);
}

function applyReaction(turns: ChatTurn[], reaction: ReactionDetails): boolean {
  const index = turns.findIndex(turn => (turn.role === 'you' || turn.role === 'elowen') && turn.id === reaction.messageId);
  const turn = turns[index];
  if (turn?.role !== 'you' && turn?.role !== 'elowen') return false;
  const reactions = reactionChipsAfter(turn.reactions, reaction);
  if (reactions === turn.reactions) return false;
  turns[index] = { ...turn, reactions };
  return true;
}

/** Fold one brain event into the view. Pure: returns a new ChatView, never mutates the input. */
export function reduce(view: ChatView, e: TranscriptEvent): ChatView {
  if (view.conversationAppendCount) {
    const { conversationAppendCount, ...active } = view;
    active.turns = view.turns.slice(0, -conversationAppendCount);
    const next = reduce(active, e);
    if (next === active) return view;
    if (e.type === 'session') return next;
    return { ...next, turns: [...next.turns, ...view.turns.slice(-conversationAppendCount)], conversationAppendCount };
  }
  if (e.type === 'retry') {
    const previous = view.modelRetry;
    if (previous && previous.target.kind === e.retry.target.kind && previous.target.id === e.retry.target.id
      && (e.retry.generation < previous.generation
        || e.retry.generation === previous.generation && e.retry.sequence <= previous.sequence)) return view;
    return { ...view, modelRetry: e.retry };
  }
  if (e.type === 'turn-boundary') {
    if (view.boundaryIds?.includes(e.id)) return view;
    return { ...view, pendingAddress: undefined, pendingTrigger: e.trigger === 'delivery' ? 'delivery' : undefined,
      boundaryIds: [...(view.boundaryIds ?? []), e.id] };
  }
  const folded = foldEvent(view, e);
  if (folded === view) return view;
  const next = { ...folded, pendingAddress: view.pendingAddress, modelRetry: e.type === 'session' || e.type === 'idle' || e.type === 'error' || e.type === 'discard_user'
    ? null : view.modelRetry };
  if ((e.type === 'tool_end' && !e.isError || e.type === 'tool_output') && e.address) next.pendingAddress = e.address;
  if (e.type === 'text' && e.delta.trim() && next.pendingAddress) {
    const index = next.turns.length - 1;
    const turn = next.turns[index];
    if (turn?.role === 'elowen') next.turns[index] = { ...turn, ...next.pendingAddress };
    next.pendingAddress = undefined;
  }
  if (e.type === 'user' || e.type === 'idle' || e.type === 'error' || e.type === 'session'
    || e.type === 'discard_user' || e.type === 'notice' && e.kind === 'compaction') next.pendingAddress = undefined;
  const firstStep = next.turns.findIndex((turn, index) => turn.role === 'elowen' && index >= view.turns.length);
  if (view.pendingTrigger && firstStep >= 0) {
    const turn = next.turns[firstStep] as ElowenTurn;
    next.turns[firstStep] = { ...turn, trigger: view.pendingTrigger };
  }
  if (e.type !== 'session') {
    next.boundaryIds = view.boundaryIds;
    next.pendingTrigger = firstStep >= 0 || e.type === 'user' || e.type === 'idle' || e.type === 'error'
      ? undefined : view.pendingTrigger;
  }
  const compacting = e.type === 'notice' && e.kind === 'compaction' ? !e.done
    : e.type === 'error' || e.type === 'session' ? false
      : view.compacting;
  if (compacting === undefined || (!compacting && view.compacting === undefined)) return next;
  return { ...next, compacting };
}

function foldEvent(view: ChatView, e: TranscriptEvent): ChatView {
  const turns = view.turns.slice();
  const ensureElowen = (): ElowenTurn => {
    const last = turns[turns.length - 1];
    if (last && last.role === 'elowen' && last.streaming) {
      const clone: ElowenTurn = {
        ...last, segments: [...last.segments], streaming: true,
        composing: last.composing, composingTool: last.composingTool,
        composingDetail: last.composingDetail, composingReason: last.composingReason,
      };
      turns[turns.length - 1] = clone;
      return clone;
    }
    const fresh: ElowenTurn = { role: 'elowen', segments: [], streaming: true, settled: false };
    turns.push(fresh);
    return fresh;
  };
  const addText = (t: ElowenTurn, delta: string): void => {
    const tail = t.segments[t.segments.length - 1];
    if (tail?.kind === 'text') t.segments[t.segments.length - 1] = { kind: 'text', text: tail.text + delta };
    else t.segments.push({ kind: 'text', text: delta });
  };
  const addReasoning = (t: ElowenTurn, delta: string): void => {
    const tail = t.segments[t.segments.length - 1];
    if (tail?.kind === 'reasoning') t.segments[t.segments.length - 1] = { kind: 'reasoning', text: tail.text + delta };
    else t.segments.push({ kind: 'reasoning', text: delta });
  };
  const patchExisting = (id: string | undefined, patch: (item: ToolItem) => ToolItem): void => {
    if (id && attachToToolInTurns(turns, id, () => true, patch)) return;
    attachToTool(ensureElowen(), id, patch);
  };
  switch (e.type) {
    case 'text': {
      addText(ensureElowen(), e.delta);
      return { turns, thinking: true, notice: undefined };
    }
    case 'reasoning': {
      addReasoning(ensureElowen(), e.delta);
      return { turns, thinking: true, notice: view.notice };
    }
    case 'notice': {
      return { turns, thinking: view.thinking, notice: e.done ? undefined : e.message };
    }
    case 'tool_authoring': {
      const t = ensureElowen();
      if (t.composing && t.composingTool === e.name && t.composingDetail === e.detail && t.composingReason === e.reason) return view;
      t.composing = true;
      t.composingTool = e.name;
      t.composingDetail = e.detail;
      t.composingReason = e.reason;
      return { turns, thinking: true, notice: view.notice };
    }
    case 'tool': {
      const t = ensureElowen();
      t.composing = false;
      t.composingTool = undefined;
      t.composingDetail = undefined;
      t.composingReason = undefined;
      const item: ToolItem = { name: e.name, detail: e.detail, pending: true, startedAt: e.startedAt, reason: e.reason, ...(e.id ? { id: e.id } : {}), ...(e.command ? { command: e.command } : {}) };
      const tail = t.segments[t.segments.length - 1];
      if (tail?.kind === 'tools') t.segments[t.segments.length - 1] = { kind: 'tools', items: [...tail.items, item] };
      else t.segments.push({ kind: 'tools', items: [item] });
      return { turns, thinking: true, notice: view.notice };
    }
    case 'step': {
      const tail = turns.at(-1);
      if (tail?.role !== 'elowen' || !tail.streaming) return view;
      settleTail(turns);
      return { ...view, turns };
    }
    case 'tool_progress': {
      // Live rolling tail of a running Bash — attach to its in-progress tool pill by id so the
      // dock shows output as it streams. Superseded by the final `tool_output`/`diff` below.
      patchExisting(e.id, (item) => ({ ...item, progress: e.text }));
      return { turns, thinking: true, notice: view.notice };
    }
    case 'diff': {
      // The final block supersedes any live `progress` tail (reconcile → no doubled dump).
      patchExisting(e.id, ({ progress: _drop, ...item }) => ({ ...item, pending: false, durationMs: e.durationMs, diff: e.diff, ...(e.output ? { output: e.output } : {}), ...(e.questionAnswer ? { questionAnswer: e.questionAnswer } : {}) }));
      return { turns, thinking: true, notice: view.notice };
    }
    case 'tool_output': {
      // The final output supersedes any live `progress` tail (reconcile → no doubled dump).
      patchExisting(e.id, ({ progress: _drop, ...item }) => ({ ...item, pending: false, durationMs: e.durationMs, output: item.command && !e.output.command ? { ...e.output, command: item.command } : e.output, ...(e.plan ? { plan: e.plan } : {}), ...(e.questionAnswer ? { questionAnswer: e.questionAnswer } : {}), ...(e.detail ? { detail: e.detail } : {}) }));
      return { turns, thinking: true, notice: view.notice };
    }
    case 'tool_end': {
      // A settled `detail` says what the call did; it replaces the argument detail the row started with.
      patchExisting(e.id, (item) => ({ ...item, pending: false, durationMs: e.durationMs, ...(e.plan ? { plan: e.plan } : {}), ...(e.questionAnswer ? { questionAnswer: e.questionAnswer } : {}), ...(e.detail ? { detail: e.detail } : {}) }));
      return { turns, thinking: true, notice: view.notice };
    }
    case 'image': {
      patchExisting(e.id, (item) => ({ ...item, pending: false, durationMs: e.durationMs, ...(e.questionAnswer ? { questionAnswer: e.questionAnswer } : {}) }));
      // A shared image is its own segment, never merged into a neighbour: two pictures in a row are two
      // pictures, and a caption belongs to exactly one of them.
      const t = ensureElowen();
      t.segments.push({ kind: 'image', image: imageFromRef(e.ref), ...(e.caption ? { caption: e.caption } : {}), ...(e.preview ? { preview: true } : {}), ...(e.deliveredAt !== undefined ? { deliveredAt: e.deliveredAt } : {}) });
      return { turns, thinking: true, notice: view.notice };
    }
    case 'view': {
      const turnIndex = turns.findIndex(turn => turn.role === 'elowen'
        && turn.segments.some(segment => segment.kind === 'view' && segment.id === e.id));
      const t = turnIndex >= 0 ? turns[turnIndex] as ElowenTurn : ensureElowen();
      // The result replaces the transient ShowView row. Its approval still uses the same tool
      // record as every other permission result, beside the view, including after a durable wait.
      const segments = t.segments.map(segment => segment.kind === 'tools'
        ? { ...segment, items: segment.items.filter(item => item.id !== e.id) } : segment);
      const replacement: Segment[] = [
        ...(e.questionAnswer ? [{ kind: 'tools' as const, items: [{
          name: 'ShowView', id: e.id, pending: false, questionAnswer: e.questionAnswer,
        }] }] : []),
        { kind: 'view', id: e.id, view: e.view, waiting: e.waiting, ...(e.deliveredAt !== undefined ? { deliveredAt: e.deliveredAt } : {}) },
      ];
      const index = segments.findIndex(segment => segment.kind === 'view' && segment.id === e.id);
      if (index >= 0) segments.splice(index, 1, ...replacement);
      else segments.push(...replacement);
      if (turnIndex >= 0) turns[turnIndex] = { ...t, segments };
      else t.segments = segments;
      return { ...view, turns };
    }
    case 'file': {
      patchExisting(e.id, (item) => ({ ...item, pending: false, durationMs: e.durationMs, ...(e.questionAnswer ? { questionAnswer: e.questionAnswer } : {}) }));
      const t = ensureElowen();
      t.segments.push({ kind: 'file', file: { url: e.ref.startsWith('/api/') ? e.ref.slice(4) : e.ref, name: e.name, size: e.size }, ...(e.caption ? { caption: e.caption } : {}), ...(e.deliveredAt !== undefined ? { deliveredAt: e.deliveredAt } : {}) });
      return { turns, thinking: true, notice: view.notice };
    }
    case 'blocks': {
      // Skip the durable twin already seeded from history; otherwise the blocks belong under the reply
      // being streamed, exactly where the reload path attaches the same stored entry.
      if (turns.some((turn) => turn.role === 'elowen' && turn.segments.some((segment) => segment.kind === 'blocks' && segment.id === e.id))) return view;
      ensureElowen().segments.push({ kind: 'blocks', id: e.id, blocks: e.blocks });
      return { ...view, turns };
    }
    case 'message-stored': {
      // A live turn takes the id its stored entry has, the one reactions, reply quotes and a reload use: the
      // echoed user turn by its admission id, an assistant message as the streaming tail it just filled. A
      // message that streamed nothing has no turn of its own, and the settled tail before it keeps its id.
      if (e.durableId) {
        const index = turns.findLastIndex((turn) => turn.role === 'you' && turn.id === e.durableId);
        if (index < 0) return view;
        turns[index] = { ...turns[index] as YouTurn, id: e.id, ...(e.branchDepth === undefined ? {} : { branchDepth: e.branchDepth }) };
        return { ...view, turns };
      }
      const tail = turns.at(-1);
      if (tail?.role !== 'elowen' || !tail.streaming || tail.id) return view;
      turns[turns.length - 1] = { ...tail, id: e.id, ...(e.branchDepth === undefined ? {} : { branchDepth: e.branchDepth }) };
      return { ...view, turns };
    }
    case 'subagent': {
      // Background children can finish after the parent assistant turn is already settled. Patch that
      // historical delegate row by id; do not fabricate an empty streaming turn or re-enable thinking.
      const { type: _type, id, ...sub } = e;
      const patched = attachToToolInTurns(turns, id, isSubagentToolName, (item) => {
        const prior = item.sub;
        if (prior && prior.steerId === sub.steerId
          && (prior.steerState === 'delivered' || prior.steerState === 'dropped')
          && sub.steerState === 'queued') {
          return { ...item, sub: { ...sub, steerState: prior.steerState, steerReason: prior.steerReason } };
        }
        return { ...item, sub };
      });
      return patched ? { ...view, turns } : view;
    }
    case 'workflow': {
      // Like `subagent`: a background workflow can report after the parent turn settled, so patch the
      // existing WorkflowStart row by call id rather than opening a streaming turn for it.
      const { type: _type, ...wf } = e;
      const patched = attachToToolInTurns(turns, e.toolCallId, (name) => name === 'WorkflowStart', (item) => ({ ...item, wf }));
      return patched ? { ...view, turns } : view;
    }
    case 'session': {
      // The server moved this message into a different conversation mid-send. Reset the
      // transcript — the daemon re-emits the triggering message as a `user` event and streams its reply,
      // so the fresh conversation rebuilds purely from the stream (no optimistic local 'you' to preserve).
      return { turns: [], thinking: false };
    }
    case 'chat-event': {
      if (e.event.details.kind === 'reaction') {
        return applyReaction(turns, e.event.details) ? { ...view, turns } : view;
      }
      const item: SessionEventItem = { id: e.event.id, event: e.event };
      // Skip the durable twin already seeded from history; only a genuinely new row changes anything.
      if (turns.some((turn) => turn.role === 'event' && turn.events.some((seen) => seen.id === item.id))) return view;
      const last = turns[turns.length - 1];
      // Extend the run in place when markers arrive back to back, so they render as one block.
      if (last?.role === 'event') {
        turns[turns.length - 1] = { ...last, events: [...last.events, item] };
        return { ...view, turns };
      }
      // A fresh marker displaces a streaming assistant tail: settle it, because the next thing the model
      // says opens a turn of its own (`ensureElowen`) while `idle` only ever settles the LAST turn.
      settleTail(turns);
      turns.push({ role: 'event', events: [item], ...(item.id ? { id: item.id } : {}) });
      // A marker is not the turn ending: `thinking` and any runtime notice stay exactly as they were.
      return { ...view, turns };
    }
    case 'user': {
      if (e.durableId && turns.some((turn) => turn.role === 'you' && turn.id === e.durableId)) return view;
      settleTail(turns);
      // The daemon's authoritative render of the user's turn (every real user send — normal or queued
      // delivery). The client no longer echoes optimistically, so this is what shows the 'you' bubble.
      // Keep the durable id on the turn so an Esc/Stop-before-output `discard_user` can pull this bubble.
      return {
        turns: [...turns, {
          role: 'you',
          settled: false,
          text: e.text,
          ...(e.author ? { author: e.author } : {}),
          ...(e.replyTo ? { replyTo: e.replyTo } : {}),
          ...(e.mentions ? { mentions: e.mentions } : {}),
          ...(e.durableId ? { id: e.durableId } : {}),
          // Preserve the echo's references and UTC announcement time in live replay snapshots.
          // Settled history later supplies the journal's display timestamp.
          ...(e.images?.length ? { images: e.images } : {}),
          ...(e.attachments?.length ? { attachments: e.attachments } : {}),
          ...(e.voiceCall ? { voiceCall: e.voiceCall } : {}),
          ...(e.createdAt ? { createdAt: e.createdAt } : {}),
        }],
        thinking: true,
        notice: view.notice,
      };
    }
    case 'discard_user': {
      // The daemon cancelled this user turn before it produced output: drop the matching 'you' bubble and
      // stop the spinner. The composer restore is the provider's job (it owns the input state).
      let index = -1;
      for (let cursor = turns.length - 1; cursor >= 0; cursor--) {
        const turn = turns[cursor]!;
        if (turn.role === 'you' && turn.id === e.durableId) { index = cursor; break; }
        if (turn.role !== 'event') break;
      }
      // No match — a duplicate discard (double Esc) or a turn already gone. Leave the view untouched:
      // forcing thinking:false here would kill the spinner of a turn the user has meanwhile resent.
      if (index === -1) return view;
      turns.splice(index, 1);
      return { turns, thinking: false, notice: undefined };
    }
    case 'idle': {
      const index = turns.findLastIndex(turn => turn.role === 'elowen' || turn.role === 'you' || turn.role === 'divider');
      const last = turns[index];
      if (last && last.role === 'elowen') turns[index] = {
        ...last, streaming: false,
        composing: false, composingTool: undefined, composingDetail: undefined, composingReason: undefined,
        ...(e.completedAt ? { createdAt: e.completedAt } : {}),
        ...(e.durationMs != null ? { durationMs: e.durationMs } : {}),
        ...(e.model ? { model: e.model } : {}),
      };
      return { turns, thinking: false, notice: undefined };
    }
    case 'error': {
      const t = ensureElowen();
      addText(t, `\n[error: ${e.message}]`);
      t.streaming = false;
      t.composing = false;
      t.composingTool = undefined;
      t.composingDetail = undefined;
      t.composingReason = undefined;
      // Retire unfinished tool calls only after the latest user turn, including calls before a
      // mid-turn marker. Earlier runs and independent child/workflow progress keep their lifecycle.
      for (let i = turns.length - 1; i >= 0; i--) {
        const turn = turns[i]!;
        if (turn.role === 'you') break;
        if (turn.role !== 'elowen') continue;
        turns[i] = { ...turn, segments: turn.segments.map(segment => segment.kind === 'tools'
          ? { ...segment, items: segment.items.map(item => item.pending ? { ...item, pending: false } : item) }
          : segment) };
      }
      return { turns, thinking: false, notice: undefined };
    }
    default:
      return view;
  }
}

function attachToTool(t: ElowenTurn, id: string | undefined, patch: (item: ToolItem) => ToolItem): void {
  for (let i = t.segments.length - 1; i >= 0; i--) {
    const seg = t.segments[i]!;
    if (seg.kind !== 'tools') continue;
    const index = id ? seg.items.findIndex((item) => item.id === id) : seg.items.length - 1;
    if (index < 0) continue;
    const items = seg.items.slice();
    items[index] = patch(items[index]!);
    t.segments[i] = { kind: 'tools', items };
    return;
  }
}

function attachToToolInTurns(
  turns: ChatTurn[],
  id: string,
  acceptsName: (name: string) => boolean,
  patch: (item: ToolItem) => ToolItem,
): boolean {
  for (let ti = turns.length - 1; ti >= 0; ti--) {
    const turn = turns[ti]!;
    if (turn.role !== 'elowen') continue;
    for (let si = turn.segments.length - 1; si >= 0; si--) {
      const seg = turn.segments[si]!;
      if (seg.kind !== 'tools') continue;
      const ii = seg.items.findLastIndex((item) => item.id === id && acceptsName(item.name));
      if (ii < 0) continue;
      const items = seg.items.slice();
      items[ii] = patch(items[ii]!);
      const segments = turn.segments.slice();
      segments[si] = { kind: 'tools', items };
      turns[ti] = { ...turn, segments };
      return true;
    }
  }
  return false;
}

/** Collect the delegated sub-agents across the whole transcript, one row per child session — the source
 *  for the agents table + drill-in. The daemon and the CLI share the rule itself (`laterChildRunSpeaks`
 *  in src/brain/subagentRuns.ts). The display fold keeps its browser-safe projection here; run ownership
 *  remains on the daemon and indexed CLI adapter.
 *
 *  A child can hold SEVERAL calls at once: the Delegate that is still working plus a DelegateContinue
 *  whose message was steered into that running turn, which returns within a second carrying neither the
 *  child's model nor its elapsed time. Latest-state-wins per CHILD let whichever call was rendered last
 *  speak for the whole sub-agent, so a still-running call is preferred here and a terminal one speaks
 *  only when no call on that child is running. Per CALL it stays latest-wins: one tool call id can appear
 *  twice (a synthetic running anchor and the real row it re-attaches to) and only the later one is current. */
/** @externalConsumer Byte-pinned browser mirror uses this canonical export in BrainChatProvider. */
export function collectSubagents(turns: ChatTurn[]): SubagentState[] {
  const byCall = new Map<string, SubagentState>();
  for (let ti = 0; ti < turns.length; ti++) {
    const turn = turns[ti]!;
    if (turn.role !== 'elowen') continue;
    for (let si = 0; si < turn.segments.length; si++) {
      const seg = turn.segments[si]!;
      if (seg.kind !== 'tools') continue;
      for (let ii = 0; ii < seg.items.length; ii++) {
        const item = seg.items[ii]!;
        if (item.sub?.sessionId) byCall.set(item.id ?? `at:${ti}:${si}:${ii}`, item.sub);
      }
    }
  }
  const bySession = new Map<string, SubagentState>();
  for (const sub of byCall.values()) {
    const speaking = bySession.get(sub.sessionId);
    if (!speaking || sub.status === 'running' || speaking.status !== 'running') bySession.set(sub.sessionId, sub);
  }
  return [...bySession.values()];
}

/** Collect the workflows across the whole transcript (one per workflow id, latest state wins) — the
 *  source for the rail's workflow section. Mirrors the CLI's `TranscriptModel.workflows()` projection. */
/** @externalConsumer Byte-pinned browser mirror uses this canonical export in BrainChatProvider. */
export function collectWorkflows(turns: ChatTurn[]): WorkflowState[] {
  const byId = new Map<string, WorkflowState>();
  for (const turn of turns) {
    if (turn.role !== 'elowen') continue;
    for (const seg of turn.segments) {
      if (seg.kind !== 'tools') continue;
      for (const item of seg.items) if (item.wf) byId.set(item.wf.id, item.wf);
    }
  }
  return [...byId.values()];
}


/** How much of the assistant's current prose {@link liveNarration} hands to a plugin surface. A narration
 *  is a glance, not a transcript: three lines of it is what an overlay can show, and the cap is what keeps
 *  a long reply from growing the string (and the re-render it causes) without bound as it streams. */
export const NARRATION_MAX_CHARS = 240;

/** The assistant prose a user can read RIGHT NOW, for a surface that covers the transcript (a plugin
 *  chat dock view expanded over the dock). It is a projection of what the dock already renders — the same
 *  segment strings, never a second composition — reduced to the one thing such a surface can carry.
 *
 *  Only `text` segments, and only from the newest assistant turn: reasoning is hidden content the reader
 *  has not asked to see, a tool row is not prose, and a turn that has scrolled past is not what is being
 *  said now. A newer `you` turn answers empty rather than the previous reply, so a covering surface can
 *  never show text from a question the user has already moved on from; session switches clear the turns
 *  themselves, so nothing survives one. Trailing event/divider turns are skipped when finding the
 *  newest visible response — a model switch mid-reply is not the conversation moving on.
 *
 *  The LAST text segment wins: prose, a tool call, then more prose is the model talking again, and the
 *  newest sentence is the one that describes what is happening. Whitespace is collapsed so the cap counts
 *  what a reader sees, and an over-long narration keeps its TAIL (from the next word boundary) because
 *  the end of a streaming sentence is the live part. */
/** @externalConsumer Byte-pinned browser mirror uses this canonical export in BrainChatProvider. */
export function liveNarration(turns: ChatTurn[]): string {
  const last = turns.findLast((turn) => turn.role !== 'event' && turn.role !== 'divider');
  if (last?.role !== 'elowen') return '';
  const spoken = last.segments.findLast((segment) => segment.kind === 'text' && segment.text.trim() !== '');
  if (!spoken || spoken.kind !== 'text') return '';
  const text = spoken.text.replace(/\s+/g, ' ').trim();
  if (text.length <= NARRATION_MAX_CHARS) return text;
  const tail = text.slice(text.length - NARRATION_MAX_CHARS);
  const space = tail.indexOf(' ');
  // One unbroken token longer than the cap (a URL, a base64 blob) has no boundary to cut at: keep it whole
  // rather than returning nothing.
  return space < 0 ? tail : tail.slice(space + 1).trim();
}


/** Settle the streaming assistant tail before appending another turn. Returns true only when replaced. */
export function settleTail(turns: ChatTurn[]): boolean {
  const last = turns.at(-1);
  if (last?.role !== 'elowen' || !last.streaming) return false;
  turns[turns.length - 1] = {
    ...last, streaming: false, composing: false, composingTool: undefined, composingDetail: undefined, composingReason: undefined,
  };
  return true;
}
